import React from 'react'
import clsx from 'clsx'
import { cloneDeep } from 'lodash'
import { TextareaAutosize } from '@mui/material'
import { ArrowDown2 } from 'iconsax-react'

import BaseModal from '../../../../components/molecules/ChatModals/BaseModal2'
import ButtonMenu from '../../../../components/molecules/ButtonMenu2'
import MultioptionEntry from './MultioptionEntry'
import UserEntry from './UserEntry'
import CustomFieldEntry from './CustomFieldEntry'
import Button from '../../../../components/atoms/Button2'
import FlowFieldEntry from './FlowFieldEntry'
import Tooltip from '@SonarCX/components/atoms/Tooltip'

import { formatForUpload } from './helpers'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import useStyles from './styles'

type FieldModalProps = {
  field: APICustomField
  editing?: boolean
  onClose: () => void
  onSave: (field: APICustomField) => void
}

const FIELD_OPTIONS: Record<APICustomField['field_type'], string> = {
  text: 'Single line input',
  multiline: 'Multi line input',
  'date-only': 'Day picker',
  'date-time': 'Day and time picker',
  'month-year': 'Month and year picker',
  dropdown: 'Dropdown',
  multiselect: 'Multiple select dropdown',
  boolean: 'Checkbox',
  user: 'Person',
  table: 'Table',
  phone: 'Phone',
  flow: 'Flow'
}
const option_fields: APICustomField['field_type'][] = [
  'dropdown',
  'multiselect',
  'table'
]

const FieldModal: React.FC<FieldModalProps> = ({
  field,
  editing,
  onClose,
  onSave
}) => {
  const isLegacy = !!field.options
  const { isMedium } = useBreakpoint()

  const [state, setState] = React.useState<APICustomField>({
    ...field,
    description: field.description ?? '',
    options: isLegacy ? field.options ?? [] : null,
    //@ts-ignore: enable temporary support for legacy fields, will be removed.
    config: isLegacy
      ? field.field_type === 'user' || field.field_type === 'flow'
        ? field.config
        : 'null'
      : {
          id: ['user', 'flow'].includes(field.field_type)
            ? field.config?.id ?? 'null'
            : 'null',
          options: field.config?.options ?? []
        }
  })

  const classes = useStyles()

  const [isButtonDisabled, reason] = React.useMemo(() => {
    if (state.title.trim().length === 0) {
      return [true, "Title can't be empty"]
    }

    if (state.field_type === 'flow' && !state.config) {
      return [true, 'Flow fields must have a flow']
    }

    if (isLegacy) {
      if (state.field_type === 'table' && state.options.length === 0) {
        return [true, 'Table fields must have at least one column']
      }

      return [false, '']
    }

    if (
      ['table', 'dropdown', 'multiselect'].includes(state.field_type) &&
      state.config.options.length === 0
    ) {
      if (state.field_type === 'table') {
        return [true, 'Table fields must have at least one column']
      }

      return [true, 'Field must have at least one option']
    }

    return [false, '']
  }, [state, isLegacy])

  // Update values locally
  const handleChange = React.useCallback(
    (prop: string) => (value: any) => {
      if (isLegacy) {
        setState(old => {
          const updated = cloneDeep(old)
          updated[prop] = value

          const hasNoOptions =
            prop === 'field_type' && !option_fields.includes(value)
          if (hasNoOptions) {
            updated.options = []
          }

          return updated
        })
        return
      }

      setState(old => {
        const updated = cloneDeep(old)
        if (prop === 'config.id') {
          if (!updated.config) {
            updated.config = { id: '', options: [] }
          }
          updated.config.id = value
        } else {
          updated[prop] = value
        }

        const hasNoOptions =
          prop === 'field_type' && !option_fields.includes(value)
        if (hasNoOptions) {
          updated.config.options = []
        }

        return updated
      })
    },
    [isLegacy]
  )

  return (
    <BaseModal
      open
      title={editing ? 'Edit Field' : 'Create Field'}
      onClose={e => {
        e.stopPropagation()
        onClose()
      }}
      PaperProps2={{
        style: {
          padding: '20px 16px 24px 16px',
          border: '1px solid #C6C6C6',
          borderRadius: '4px',
          width: isMedium ? 'calc(100% - 4rem)' : '100%',
          maxWidth: isMedium ? 'unset' : '480px',
          height: isMedium && `auto`,
          maxHeight: isMedium && 'unset',
          minHeight: isMedium && 'auto'
        }
      }}
      classes={{ title: classes.title, close: classes.close }}
    >
      <div className={classes.rows}>
        <div className={classes.row}>
          <div className={classes.label}>Title</div>

          <input
            autoFocus
            value={state.title}
            placeholder='Title'
            autoComplete='off'
            onChange={e => handleChange('title')(e.target.value)}
            className={classes.input}
            style={{ marginLeft: 9 }}
            maxLength={50}
          />
        </div>

        <div className={classes.row}>
          <div className={classes.label}>Field Type</div>
          <ButtonMenu
            title='Field Type'
            options={Object.keys(FIELD_OPTIONS).map(name => ({ name }))}
            onSelect={handleChange('field_type')}
            renderOption={opt => FIELD_OPTIONS[opt]}
            className={classes.menuRoot}
            menuProps={{
              anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
              transformOrigin: { horizontal: 'center', vertical: 'top' }
            }}
          >
            {FIELD_OPTIONS[state.field_type]}
            <ArrowDown2 className={classes.icon} />
          </ButtonMenu>
        </div>

        <div className={clsx(classes.row, classes.column)}>
          <div className={classes.label}>Description</div>

          <TextareaAutosize
            autoComplete='off'
            minRows={1}
            maxRows={5}
            maxLength={300}
            placeholder='Description'
            value={state.description}
            onChange={e => handleChange('description')(e.target.value)}
            className={clsx(classes.input, classes.description)}
          />
        </div>

        {/* Dropdown and multiselect option entry */}
        {['dropdown', 'multiselect'].includes(state.field_type) && (
          <MultioptionEntry state={state} setState={setState} />
        )}

        {/* User config entry */}
        {state.field_type === 'user' && (
          <UserEntry state={state} handleChange={handleChange} />
        )}

        {/* Table column options */}
        {state.field_type === 'table' && (
          <CustomFieldEntry state={state} setState={setState} />
        )}

        {/* Flow column options */}
        {state.field_type === 'flow' && (
          <FlowFieldEntry state={state} setState={setState} />
        )}
      </div>

      <Tooltip title={reason}>
        <div>
          <Button
            onClick={() => onSave(formatForUpload(state))}
            disabled={isButtonDisabled}
            className={classes.button}
          >
            {editing ? 'Save' : 'Add'} Field
          </Button>
        </div>
      </Tooltip>
    </BaseModal>
  )
}

export default FieldModal
